import React, { useState, useEffect } from 'react';
import { MdNavigateBefore, MdNavigateNext } from 'react-icons/md';
import { AnimatePresence, motion } from 'framer-motion';
import { getCurrentWeekStart } from '../../utils/getCurrentWeekStart';

export interface WeekNavProps {
  weekStart: Date;
  weekEnd?: Date;
  isCustom?: boolean;
  onChangeWeek: (offset: number) => void;
  onClickThisWeek?: () => void;
  fullWidth?: boolean;
}

const formatDDMM = (d: Date) =>
  `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;

export const WeekNav: React.FC<WeekNavProps> = ({
  weekStart,
  weekEnd,
  isCustom = false,
  onChangeWeek,
  onClickThisWeek,
  fullWidth = true,
}) => {
  const [label, setLabel] = useState('');
  const [direction, setDirection] = useState(0); // +1 for next, -1 for prev

  const current = getCurrentWeekStart();

  useEffect(() => {
    let newLabel = '';
    if (isCustom && weekEnd) {
      newLabel = `${formatDDMM(weekStart)} – ${formatDDMM(weekEnd)}`;
    } else {
      const diff = Math.round(
        (weekStart.getTime() - current.getTime()) / (7 * 24 * 60 * 60 * 1000)
      );
      if (diff === 0) newLabel = 'This Week';
      else if (diff === 1) newLabel = 'Next Week';
      else if (diff === -1) newLabel = 'Previous Week';
      else {
        const end = new Date(weekStart);
        end.setDate(end.getDate() + 6);
        newLabel = `${formatDDMM(weekStart)} – ${formatDDMM(end)}`;
      }
    }
    setLabel(newLabel);
  }, [weekStart, weekEnd, isCustom]);

  const handleChange = (offset: number) => {
    setDirection(offset);
    onChangeWeek(offset);
  };

  const widthClasses = fullWidth ? 'w-full max-w-md mx-auto' : 'flex-1';
  const buttonBase =
    'relative flex-shrink-0 w-10 h-10 flex items-center justify-center';
  const buttonOverlay =
    'absolute inset-[-32px] rounded-full bg-gray-300 opacity-0 active:opacity-30 transition duration-150';

  const variants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 50 : -50,
      opacity: 0,
    }),
    center: { x: 0, opacity: 1 },
    exit: (dir: number) => ({
      x: dir > 0 ? -50 : 50,
      opacity: 0,
    }),
  };

  return (
    <div
      className={`flex items-center justify-between ${widthClasses} rounded-full overflow-hidden border border-gray-300`}
    >
      <button
        onClick={() => handleChange(-1)}
        className={`${buttonBase} group`}
        aria-label="Previous Week"
      >
        <span className={buttonOverlay}></span>
        <MdNavigateBefore className="w-5 h-5 relative z-10 text-black transition-transform group-active:scale-90" />
      </button>

      <div className="flex-[0_1_120px] text-sm font-medium text-black text-center relative h-6 overflow-hidden">
        <AnimatePresence custom={direction} mode="wait">
          <motion.div
            key={label}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.2 }}
            className="absolute inset-0 flex items-center justify-center"
          >
            {label}
          </motion.div>
        </AnimatePresence>
      </div>

      <button
        onClick={() => handleChange(1)}
        className={`${buttonBase} group`}
        aria-label="Next Week"
      >
        <span className={buttonOverlay}></span>
        <MdNavigateNext className="w-5 h-5 relative z-10 text-black transition-transform group-active:scale-90" />
      </button>
    </div>
  );
};
